/**
 * Normalize error text from backend JSON bodies.
 * Supports: { message }, { error }, { success, message }, nested { data },
 * express-validator { errors: [] }, mongoose-style { errors: {} }.
 */

function pushUnique(out, str) {
  if (typeof str !== 'string') return;
  const t = str.trim();
  if (t && !out.includes(t)) out.push(t);
}

function collectFromObject(data, out, depth = 0) {
  if (!data || typeof data !== 'object' || depth > 4) return;

  if (typeof data.message === 'string') pushUnique(out, data.message);
  if (typeof data.error === 'string') pushUnique(out, data.error);

  if (data.error && typeof data.error === 'object' && typeof data.error.message === 'string') {
    pushUnique(out, data.error.message);
  }

  if (data.data && typeof data.data === 'object') {
    if (typeof data.data.error === 'string') pushUnique(out, data.data.error);
    if (typeof data.data.message === 'string') pushUnique(out, data.data.message);
  }

  if (Array.isArray(data.errors)) {
    data.errors.forEach((e) => {
      if (typeof e === 'string') pushUnique(out, e);
      else if (e && typeof e === 'object') {
        if (typeof e.msg === 'string') pushUnique(out, e.msg);
        else if (typeof e.message === 'string') pushUnique(out, e.message);
      }
    });
  }

  if (data.errors && typeof data.errors === 'object' && !Array.isArray(data.errors)) {
    Object.entries(data.errors).forEach(([field, v]) => {
      if (typeof v === 'string') pushUnique(out, `${field}: ${v}`);
      else if (typeof v === 'object' && v?.message) pushUnique(out, String(v.message));
      else if (Array.isArray(v) && v[0]) pushUnique(out, `${field}: ${v[0]}`);
    });
  }
}

/**
 * Raw backend text from a parsed JSON body (e.g. after fetch → response.json()).
 * Returns null if nothing usable.
 */
export function getErrorMessageFromBody(data) {
  if (data == null) return null;
  if (typeof data === 'string') {
    const t = data.trim();
    return t || null;
  }
  if (typeof data !== 'object') return null;
  const out = [];
  collectFromObject(data, out);
  if (out.length === 0) return null;
  return out.join(' · ');
}

/**
 * Backend-only message from an Axios error (no fallback).
 */
export function getBackendErrorMessage(error) {
  if (!error) return null;
  const data = error.response?.data;
  if (data !== undefined && data !== null) {
    const fromBody = getErrorMessageFromBody(data);
    if (fromBody) return fromBody;
  }
  return null;
}

const NETWORK_FALLBACK = 'Unable to connect to the server. Please check your network.';

/**
 * Prefer backend message; then network hint; then caller fallback.
 */
export function getApiErrorMessage(
  error,
  fallback = 'Something went wrong. Please try again.'
) {
  const backend = getBackendErrorMessage(error);
  if (backend) return backend;

  if (!error?.response) {
    const msg = typeof error?.message === 'string' ? error.message : '';
    if (msg === 'Network Error' || msg === 'Failed to fetch') {
      return NETWORK_FALLBACK;
    }
    if (msg && !/^Request failed with status code \d+$/i.test(msg)) {
      return msg;
    }
    return NETWORK_FALLBACK;
  }

  return fallback;
}

/**
 * Attach userMessage to axios errors (backend or fallback) for optional use in catch.
 */
export function attachAxiosUserMessage(error, fallback) {
  if (!error || typeof error !== 'object') return error;
  error.userMessage = getApiErrorMessage(error, fallback);
  return error;
}

/**
 * After `fetch`, parse JSON once and return data or throw Error with backend message.
 */
export async function fetchJsonOrThrow(response, fallbackMessage = 'Request failed') {
  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    const msg = getErrorMessageFromBody(data) || fallbackMessage;
    const err = new Error(msg);
    err.status = response.status;
    err.body = data;
    throw err;
  }
  return data;
}
